import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { RootState } from '../app/store';
import { UpsellTriggers } from '../components/OmniBar/Omnibar.constants';
import { UPSELL_METRIC_VERSION } from '../constants';
import {
  AgentInterceptionType,
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import { sendSpokenPrompt } from '../reducers/spokenPromptThunk';
import { selectIsExpressUpsellSuppressionActive } from '../selectors/menu';
import {
  selectSpokenPrompt,
  selectUpsellExperimentStamp,
  selectWithheldConstraints,
} from '../redux/features/experiments/experiments.selector';
import {
  promptAsked,
  upsellOffered,
} from '../redux/features/experiments/experiments.slice';
import { selectSlotBoundOffer } from '../selectors/slotBoundUpsell';
import { toSlotBoundAttemptGroup } from '../utils/slotBoundUpsell';
import { IPromptSlot } from '../redux/features/experiments/experiments.props';
import {
  isConstraintWithheld,
  logWithheldPromptSkipped,
} from '../redux/features/experiments/experiments.utils';
import {
  isExpressSuppressedUpsellPrompt,
  isUnavailableSuppressedUpsellPrompt,
} from '../utils/cart.upsellConditions';
import logger from '../utils/logger';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { upsell } from '../utils/upsell';
import useAgentInterception from './agentInterception/useAgentInterception.hooks';
import { selectIsAgentActionBlocked } from '../redux/features/agentMode/agentMode.selector';

const useTTSMessage = () => {
  const dispatch = useAppDispatch();
  const { sendAgentInterception } = useAgentInterception();
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const isBrandMenu = getMenuUnificationFeatureFlag();
  // Non-subscribing store access: this hook is mounted in every ModGroup, and
  // subscribing to the cart slice here re-rendered every mounted group on
  // every cart dispatch — defeating ModGroup's scoped memo (HITL-1422). The
  // selected item is only needed when a message is actually sent, so read it
  // at call time.
  const store = useStore<RootState>();

  const getSelectedItem = () => {
    const state = store.getState();
    return (
      state.dialog.selectedItem ||
      Object.values(state.cart.cartItems)[
        Object.values(state.cart.cartItems).length - 1
      ]
    );
  };

  const sendTTSMessage = (message: string, metadata?: any): boolean => {
    // HITL-1606: outside Intercept the agent may not speak or send. Guarded
    // HERE, at the send, not only in useAgentInterception: these paths dispatch
    // the outbound frame FIRST and stamp the interception after, so refusing the
    // interception alone would still let the customer hear it. Reachable by
    // keyboard with no button to disable, so a refusal here is expected, not a
    // defect — debug, not warn.
    if (isAgentActionBlocked) {
      logger.debug({
        message: 'Center-pane TTS refused: the agent is not in Intercept mode',
      });
      return false;
    }
    // HITL-1587: route through the shared spoken-prompt thunk — it
    // substitutes {total} like the omnibar send, skips outright (with a
    // METRIC) when the template needs a check total and none exists, and
    // records a spoken total so Complete doesn't repeat it. Nothing was
    // spoken on a skip, so the interception is only stamped on a send.
    const { spoken } = dispatch(sendSpokenPrompt({ message, metadata }));
    if (spoken) {
      sendAgentInterception({
        data: {
          type: AgentInterceptionType.TTS_SENT,
        },
      });
    }
    return spoken;
  };

  // `slot` locates the group in the cart; without it, the selected item's top-level group.
  const sendModGroupTTSMessage = (
    modGroup: any,
    slot?: Pick<IPromptSlot, 'cartItemId' | 'pathToItem'>
  ) => {
    // Express mode must never ask a `do_not_upsell_in_express_mode`-tagged
    // upsell prompt. The add-time auto-decline and the omnibar auto-prompt
    // filter already honour that; the heading click did not, so an agent could
    // still voice a prompt the app had suppressed on every other path
    // (HITL-1529). The
    // rule lives here rather than in the two ModGroup components so a single
    // check covers every caller — the components only mirror it in the cursor
    // and tooltip. Read at call time from the non-subscribing store, like the
    // selected item below.
    if (
      isExpressSuppressedUpsellPrompt(
        modGroup,
        selectIsExpressUpsellSuppressionActive(store.getState())
      )
    ) {
      // Still emit a metric — with a message distinct from the one below, so a
      // session log shows the agent tried and the app declined, not a TTS that
      // never happened.
      dispatch(
        messagingActions.sendInfo({
          data: {
            message: `Mod group heading (${modGroup.name}) click suppressed in express mode`,
            type: 'METRIC',
          },
        } as any)
      );
      logger.debug(
        `[EXPRESS_MODE] heading click on "${modGroup.name}" suppressed (do_not_upsell_in_express_mode)`
      );
      return;
    }

    // HITL-1720: an option this prompt offers is 86'd, so asking it would
    // upsell something the store can't sell. The AI skips it the same way.
    if (isUnavailableSuppressedUpsellPrompt(modGroup)) {
      dispatch(
        messagingActions.sendInfo({
          data: {
            message: `Mod group heading (${modGroup.name}) click suppressed — upsell option 86'd`,
            type: 'METRIC',
          },
        } as any)
      );
      logger.debug(
        `[UPSELL_OPTION_86ED] heading click on "${modGroup.name}" suppressed`
      );
      return;
    }

    // Withheld for this session's experiment group: the prompt is not spoken.
    if (
      isConstraintWithheld(
        selectWithheldConstraints(store.getState()),
        modGroup.name
      )
    ) {
      dispatch(
        messagingActions.sendInfo({
          data: {
            message: `Mod group heading (${modGroup.name}) click suppressed by experiment`,
            type: 'METRIC',
          },
        } as any)
      );
      logWithheldPromptSkipped(modGroup.name, 'mod-group heading');
      return;
    }

    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        message: `Mod group heading (${modGroup.name}) is clicked`,
        type: 'METRIC',
      },
    };
    dispatch(messagingActions.sendInfo(payload as any));

    // HITL-1761: a slot with an upsell bound to it is asked as that upsell.
    if (isBrandMenu) {
      const state = store.getState();
      const slotCartItem =
        slot?.cartItemId !== undefined
          ? state.cart.cartItems[slot.cartItemId]
          : getSelectedItem();
      const slotBoundOffer = selectSlotBoundOffer(
        state,
        modGroup,
        slotCartItem?.name
      );
      if (slotBoundOffer) {
        // Credited to the slot's own item, which need not be the selected one:
        // the Drink ⓘ on combo 2 can be clicked while combo 1 is selected.
        const upsell_attempted = upsell.attempt(
          toSlotBoundAttemptGroup(slotBoundOffer, modGroup),
          slotCartItem?.name,
          UpsellTriggers.hitlCenterPane,
          slotBoundOffer.prompt,
          slotBoundOffer.name,
          undefined,
          selectUpsellExperimentStamp(state, slotBoundOffer.name),
          slotCartItem?.cartItemId
        );
        if (
          sendTTSMessage(slotBoundOffer.prompt, {
            upsell_attempted,
            upsell_metric_version: UPSELL_METRIC_VERSION,
          })
        ) {
          dispatch(upsellOffered(slotBoundOffer.name));
        }
        return;
      }
    }

    const promptSlot: IPromptSlot = {
      cartItemId: getSelectedItem()?.cartItemId,
      pathToItem: modGroup.id,
      ...slot,
      constraintName: modGroup.name,
    };
    const prompt = selectSpokenPrompt(
      store.getState(),
      promptSlot,
      modGroup.description
    );
    let message = prompt || '',
      metadata = null;

    if (isBrandMenu && modGroup?.is_upsell_prompt) {
      const upsell_attempted = upsell.attempt(
        modGroup,
        getSelectedItem()?.name,
        UpsellTriggers.hitlCenterPane,
        prompt || '.',
        modGroup.name,
        undefined,
        selectUpsellExperimentStamp(store.getState(), modGroup.name),
        getSelectedItem()?.cartItemId
      );
      metadata = {
        upsell_attempted,
        upsell_metric_version: UPSELL_METRIC_VERSION,
      };
    }

    if (sendTTSMessage(message, metadata))
      dispatch(promptAsked(promptSlot, modGroup.description));
  };

  return {
    sendModGroupTTSMessage,
    sendTTSMessage,
  };
};

export default useTTSMessage;
